import React, {useEffect, useState} from "react";
import {TextField} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import {debounce} from "@material-ui/core/utils";
import {useTranslation} from "react-i18next";
import Api from "../../store/Api";
import Contact from "../../store/models/Contact";

interface ContactRecommendedByAutocompleteProps {
    value?: string;
    setValue: (val: string | null) => void;
}


export default function ContactRecommendedByAutocomplete({value, setValue}: ContactRecommendedByAutocompleteProps) {
    const {t} = useTranslation();
    const [options, setOptions] = useState<Contact[]>([]);
    const [inputValue, setInputValue] = useState(value ?? "");
    const [selected, setSelected] = useState<Contact | null>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (value && !selected) {
            Api.getContact(value).then((c) => {
                if (cancelled) return;
                setSelected(c);
                setInputValue(c?.companyLine1 ? c.companyLine1 : inputValue);
                setOptions((prev) =>
                    prev.some((x) => x.stContactID === c.stContactID) ? prev : [...prev, c]
                );
                setDirty(false);
                setOpen(false);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [value, selected, inputValue]);

    useEffect(() => {
        const q = inputValue.trim();
        if (!dirty || q.length < 2) {
            if (dirty) {
                setOptions([]);
            }
            setLoading(false);
            return;
        }
        setLoading(true);
        const run = debounce(async (qq: string) => {
            const res = await Api.searchContacts(qq, 1);
            setOptions(res?.searchResult ?? []);
            setLoading(false);
        }, 300);
        run(q);
        return () => {
            (run as any).clear?.();
            (run as any).cancel?.();
        };
    }, [inputValue, dirty]);

    return (
        <Autocomplete
            options={options}
            filterOptions={(x) => x}
            loading={loading}
            open={open && dirty && inputValue.trim().length >= 2}
            onOpen={() => dirty && setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionLabel={(o) =>
                `${o.companyLine1 ?? ""} - ${o.address?.zipCode ?? ""} ${o.address?.place ?? ""}`
            }
            getOptionSelected={(o, v) => o.stContactID === v.stContactID}
            value={selected}
            onChange={(_e, val) => {
                setSelected(val);
                setValue(val ? val.stContactID : null);
                setDirty(false);
                setOpen(false);
            }}
            inputValue={inputValue}
            onInputChange={(_e, val, reason) => {
                setInputValue(val);
                if (reason === "input") {
                    setDirty(true);
                    setOpen(true);
                }
                if (reason === "reset" || reason === "clear") {
                    setDirty(false);
                    setOpen(false);
                }
            }}
            noOptionsText={t("contactRecommendedByAutocomplete.noOptionText")}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t("contactRecommendedByAutocomplete.label")}
                    variant="standard"
                    onPaste={(e) => {
                        const text = e.clipboardData.getData("text");
                        if (text) {
                            setInputValue(text);
                            setDirty(true);
                            setOpen(true);
                        }
                    }}
                    onFocus={() => {
                        if (dirty && inputValue.trim().length >= 2) setOpen(true);
                    }}
                />
            )}
        />
    );
}
